import { FiShoppingBag, FiArrowRight } from 'react-icons/fi'
import { appUrl } from '@astro/config'
import { useFeaturedProducts } from '../../../hooks/useFeaturedProducts'

/** Format a decimal price string as ₹XX,XXX (same convention as the shop app). */
const formatPrice = (raw) => {
  const num = Number.parseFloat(raw)
  if (Number.isNaN(num)) return null
  return '₹' + num.toLocaleString('en-IN', { maximumFractionDigits: 0 })
}

/**
 * Shop cross-sell CTA under the FAQ accordion — a handful of featured
 * products from the live catalog plus a redirect into the shop app.
 * Renders nothing until the API returns products (no dummy data).
 */
export default function ProductsCtaSection() {
  const { products } = useFeaturedProducts(4)

  if (!products.length) return null

  return (
    <section className="relative overflow-hidden py-8 bg-linear-to-b from-primary via-[#460813] to-[#2c050c] font-normal md:px-18">
      {/* Soft gold glow to lift the dark band */}
      <div className="pointer-events-none absolute inset-0 opacity-60" aria-hidden="true">
        <div className="absolute -top-24 right-1/4 h-96 w-96 rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute -bottom-24 left-1/4 h-80 w-80 rounded-full bg-secondary/5 blur-3xl" />
      </div>

      <div className="container relative z-10 mx-auto px-6 max-w-350">
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-3">
            <span className="h-px w-8 bg-secondary/50" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-secondary">Sacred Remedies</span>
            <span className="h-px w-8 bg-secondary/50" />
          </div>
          <h2 className="font-cinzel text-3xl uppercase leading-tight text-white md:text-4xl">
            Remedies From <span className="text-gradient-gold">The Shop</span>
          </h2>
          <p className="mx-auto max-w-xl text-sm leading-relaxed text-white/60">
            Energised gemstones, rudraksha and yantras — prescribed in consultations, delivered from our shop.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
          {products.map((p) => {
            const price = formatPrice(p.final_price ?? p.total_price ?? p.price)
            return (
              <a
                key={p.id}
                href={appUrl('shop', `products/${p.slug}`)}
                className="group rounded-2xl border border-white/10 bg-white/95 p-4 shadow-lg shadow-black/20 transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-black/30"
              >
                <div className="flex items-center justify-center overflow-hidden rounded-xl bg-background/60">
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt={p.name}
                      loading="lazy"
                      className="h-3/4 w-3/4 object-contain transition-transform duration-700 group-hover:scale-110"
                    />
                  ) : (
                    <FiShoppingBag className="h-8 w-8 text-primary/20" />
                  )}
                </div>
                <h3 className="mt-3 line-clamp-2 text-[13px] font-semibold leading-snug text-primary group-hover:text-secondary transition-colors">
                  {p.name}
                </h3>
                {price && <p className="mt-1 text-sm font-semibold text-secondary">{price}</p>}
              </a>
            )
          })}
        </div>

        <div className="mt-10 text-center">
          <a
            href={appUrl('shop', 'products')}
            className="group inline-flex items-center gap-2 rounded-full bg-secondary px-8 py-3.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary shadow-lg shadow-secondary/20 transition-all hover:bg-white"
          >
            <FiShoppingBag size={16} />
            Explore All Products
            <FiArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </a>
        </div>
      </div>
    </section>
  )
}
